/* Le edizioni del catalogo.
 *
 * **Importare e mettere in vigore sono due tasti.** Non e' una scomodita': chi
 * reimporta l'estrazione per correggere un refuso non deve cambiare, nello
 * stesso istante, le regole applicate a chi sta lavorando. L'importazione dice
 * a schermo di non aver attivato niente, cosi' l'attivazione resta un gesto che
 * qualcuno ha compiuto e non un effetto collaterale.
 *
 * **Quale e' in vigore si legge in elenco.** Con due edizioni presenti la
 * domanda «con quale si convalida un codice che arriva oggi» deve avere una
 * risposta a colpo d'occhio, altrimenti la si cerca nella base dati.
 *
 * **I punti aperti si aprono e si leggono.** Un numero non dice a nessuno che
 * cosa chiedere al committente. E non impediscono l'attivazione: sono domande
 * aperte sul documento, non difetti dell'estrazione, e tenere fuori vigore un
 * catalogo per esse fermerebbe il lavoro.
 */
import { useEffect, useState } from 'react'

import { ServiceError } from '../../api'
import {
  activateRelease,
  conflictLabel,
  importRelease,
  readConflicts,
  readReleases,
  type Conflict,
  type Imported,
  type Release,
} from '../../admin/catalog'
import { Hint } from '../../carta/Hint'
import { StatusPill } from '../../carta/StatusPill'
import { moment } from '../../format'
import { useLocale, useT } from '../../i18n/LocaleProvider'
import { t as translate } from '../../i18n/t'

export function CatalogAdmin(): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const [releases, setReleases] = useState<Release[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [imported, setImported] = useState<Imported | null>(null)
  const [openConflicts, setOpenConflicts] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null)

  useEffect(() => {
    let cancelled = false
    void readReleases()
      .then((found) => {
        if (!cancelled) setReleases(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'catalog.readFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  useEffect(() => {
    if (openConflicts === null) return
    setConflicts(null)
    let cancelled = false
    void readConflicts(openConflicts)
      .then((found) => {
        if (!cancelled) setConflicts(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'catalog.conflictsFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [openConflicts, locale])

  async function act(what: () => Promise<void>): Promise<void> {
    setBusy(true)
    setFailure(null)
    try {
      await what()
      setReleases(await readReleases())
    } catch (error: unknown) {
      setFailure(read(error, t('users.requestFailed')))
    } finally {
      setBusy(false)
    }
  }

  function onFile(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file === undefined) return
    void act(async () => {
      const written = await readText(file, t('catalog.fileUnreadable'))
      let payload: unknown
      try {
        payload = JSON.parse(written) as unknown
      } catch {
        // Il file si legge qui prima di partire: mandare al servizio qualcosa
        // che non e' JSON darebbe un rifiuto che parla del catalogo quando il
        // problema e' il file.
        throw new ServiceError(0, 'not_json', t('catalog.notJson'), null)
      }
      setImported(await importRelease(payload))
    })
  }

  return (
    <main className="amministrazione">
      <h2>{t('title.catalog')}</h2>
      {failure !== null && <p role="alert">{failure}</p>}

      <section aria-labelledby="importa-titolo">
        <h3 id="importa-titolo">{t('catalog.importTitle')}</h3>
        <label>
          {t('catalog.importFile')}
          <input type="file" accept="application/json,.json" disabled={busy} onChange={onFile} />
        </label>
        <p className="tenue">{t('catalog.importNote')}</p>
        {imported !== null && (
          <div role="status" className="esito-importazione">
            <p>
              {t('catalog.importedPrefix')} <strong>{imported.release}</strong> ({imported.rule}
              {imported.created ? t('catalog.importedNew') : t('catalog.importedUpdated')}).{' '}
              <strong>{t('catalog.notActivated')}</strong>
            </p>
            <p>
              {t('catalog.importStats', {
                added: total(imported.added),
                updated: total(imported.updated),
                unchanged: total(imported.unchanged),
                conflicts: imported.conflicts,
              })}
            </p>
          </div>
        )}
      </section>

      <section aria-labelledby="edizioni-titolo">
        <h3 id="edizioni-titolo">
          {t('catalog.releases')}
          <Hint about="catalogEdition" />
        </h3>
        {releases === null ? (
          <p role="status">{t('catalog.releasesLoading')}</p>
        ) : releases.length === 0 ? (
          <p>{t('catalog.releasesEmpty')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th scope="col">{t('catalog.colDocument')}</th>
                <th scope="col">{t('catalog.colActive')}</th>
                <th scope="col">{t('catalog.colContent')}</th>
                <th scope="col">{t('catalog.colOpen')}</th>
                <th scope="col">{t('catalog.colImported')}</th>
                <th scope="col">{t('catalog.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((release) => (
                <tr key={release.id}>
                  <th scope="row">
                    {release.document} v{release.version}
                    {release.title !== null && <span className="tenue"> · {release.title}</span>}
                  </th>
                  <td>
                    {release.active ? (
                      <>
                        <StatusPill label={t('catalog.active')} tone="ref" />
                        {release.activated_at !== null && (
                          <span className="tenue">
                            {t('catalog.activeSince', { date: moment(release.activated_at) })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="tenue">{t('common.no')}</span>
                    )}
                  </td>
                  <td>
                    {t('catalog.contentSummary', {
                      n: release.criticalities,
                      codes: release.swim_codes,
                    })}
                  </td>
                  <td>
                    {release.conflicts === 0 ? (
                      <span className="tenue">{t('common.noneMasc')}</span>
                    ) : (
                      <button
                        type="button"
                        aria-expanded={openConflicts === release.id}
                        onClick={() =>
                          setOpenConflicts(openConflicts === release.id ? null : release.id)
                        }
                      >
                        {t('catalog.openCount', { n: release.conflicts })}
                      </button>
                    )}
                  </td>
                  <td>{moment(release.imported_at)}</td>
                  <td>
                    {release.active ? (
                      <span className="tenue">{t('common.dash')}</span>
                    ) : (
                      <button
                        type="button"
                        className="primario"
                        disabled={busy}
                        onClick={() => void act(async () => void (await activateRelease(release.id)))}
                      >
                        {t('catalog.activate')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="tenue">{t('catalog.releasesNote')}</p>
      </section>

      {openConflicts !== null && (
        <section aria-labelledby="aperti-titolo">
          <h3 id="aperti-titolo">{t('catalog.conflictsTitle')}</h3>
          {conflicts === null ? (
            <p role="status">{t('catalog.conflictsLoading')}</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('catalog.colSubject')}</th>
                  <th scope="col">{t('catalog.colIssue')}</th>
                  <th scope="col">{t('catalog.colDetail')}</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((one, index) => (
                  <tr key={`${one.owner}-${one.kind}-${index}`}>
                    <th scope="row">
                      <code>{one.owner}</code>
                    </th>
                    <td>{conflictLabel(one.kind)}</td>
                    <td>
                      {one.detail === null || one.detail === undefined ? (
                        <span className="tenue">{t('common.dash')}</span>
                      ) : (
                        <code>{JSON.stringify(one.detail)}</code>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="tenue">{t('catalog.conflictsNote')}</p>
        </section>
      )}
    </main>
  )
}

/** Il contenuto del file, come testo.
 *
 * `FileReader` e non `Blob.text()`: l'estrazione del catalogo e' un file che si
 * legge intero una volta, e questa forma funziona anche dove `Blob.text` non
 * c'e' — cosi' la lettura del file resta provabile. */
function readText(file: File, unreadable: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error(unreadable))
    reader.readAsText(file)
  })
}

function total(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, one) => sum + one, 0)
}

function read(error: unknown, fallback: string): string {
  return error instanceof ServiceError ? error.message : fallback
}
